// src/ReportsSettings.js
import { useState, useEffect, useRef, useMemo } from 'react';
import TitleBar from './components/ui/TitleBar';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';

// ── Mobile helper ──────────────────────────────────────────────────────────
const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024;

// ✅ All reports with live backends
const LIVE_REPORT_IDS = [
  'sales_summary',
  'expenses_summary',
  'mileage_summary',
  'outstanding_invoices',
  'payment_history',
  'customer_list',
  'top_customers',
  'declined_estimates',
  'no_response_estimates',
  'inventory_reorder',
  'inventory_count_sheet',
  'quarterly_sales_tax',
  'profit_loss',
  'schedule_c',
  'discounts_coupons_catalog',
  'discounts_coupons_usage',
  'audit_trail'
];

const REPORT_CATEGORIES = [
  {
    category: 'ACCOUNTING & TAX',
    reports: [
      { id: 'audit_trail', name: 'AUDIT TRAIL', description: 'Invoice deletions and payment reversals log (date range)', icon: '🔍' },
      { id: 'expenses_summary', name: 'EXPENSES SUMMARY', description: 'Expenses by category, vendor, and detail (date range)', icon: '💸' },
      { id: 'mileage_summary', name: 'MILEAGE SUMMARY', description: 'Mileage totals and detail (date range)', icon: '🚗' },
      { id: 'profit_loss', name: 'PROFIT & LOSS STATEMENT', description: 'Revenue vs expenses with net profit calculation', icon: '📈' },
      { id: 'quarterly_sales_tax', name: 'QUARTERLY SALES TAX REPORT', description: 'Sales tax collected for the quarter — for your records', icon: '📊' },
      { id: 'schedule_c', name: 'SCHEDULE C (1040)', description: 'Self-employment tax preparation worksheet', icon: '📄' }
    ]
  },
  {
    category: 'SALES & REVENUE',
    reports: [
      { id: 'discounts_coupons_catalog', name: 'DISCOUNTS & COUPONS CATALOG', description: 'All configured discounts and coupons with types and values', icon: '🏷️' },
      { id: 'discounts_coupons_usage', name: 'DISCOUNTS & COUPONS USAGE', description: 'Total discount amounts applied on invoices by date range', icon: '📉' },
      { id: 'outstanding_invoices', name: 'OUTSTANDING INVOICES', description: 'Unpaid and overdue invoices', icon: '📋' },
      { id: 'payment_history', name: 'PAYMENT HISTORY', description: 'All payments received with details', icon: '💳' },
      { id: 'sales_summary', name: 'SALES SUMMARY', description: 'Total sales and revenue breakdown by date range', icon: '💰' }
    ]
  },
  {
    category: 'ESTIMATES',
    reports: [
      { id: 'declined_estimates', name: 'DECLINED ESTIMATES', description: 'List of estimates that were declined', icon: '❌' },
      { id: 'no_response_estimates', name: 'NO RESPONSE ESTIMATES', description: 'Estimates sent but not yet approved or declined', icon: '⏳' }
    ]
  },
  {
    category: 'CUSTOMERS',
    reports: [
      { id: 'customer_list', name: 'CUSTOMER LIST', description: 'Complete customer directory with contact info', icon: '👥' },
      { id: 'top_customers', name: 'TOP CUSTOMERS', description: 'Highest revenue customers', icon: '⭐' }
    ]
  },
  {
    category: 'INVENTORY',
    reports: [
      { id: 'inventory_count_sheet', name: 'INVENTORY COUNT SHEET', description: 'Physical inventory count worksheet', icon: '📝' },
      { id: 'inventory_reorder', name: 'INVENTORY REORDER', description: 'Low stock items that need reordering', icon: '📦' }
    ]
  }
];

const REPORT_DISCLOSURES = {
  sales_summary: 'Scope: This report includes invoices dated within the selected period. Collections and outstanding balances reflect the current payment status of those invoices, regardless of payment date. Use Payment History, Profit & Loss, or Quarterly Sales Tax for period-based cash activity.',
  top_customers: 'All-time ranking based on recorded payments, excluding the allocated sales-tax portion. Displays up to 50 customers.',
  quarterly_sales_tax: 'This cash-basis report reflects posted payments recorded in MyTool NOW for the selected quarter and the taxable or nontaxable selections saved on invoice line items. MyTool NOW does not file or remit taxes on your behalf. Verify your remittance obligations with your state tax authority or a qualified tax professional.',
  profit_loss: 'Basis: Revenue reflects posted payments received during the selected period, excluding allocated sales tax. Expense categories reflect categories recorded in MyTool NOW. Supporting records: See Payment History, Expense Summary, and Mileage Summary for supporting detail.',
  payment_history: 'Basis: Gross Collected is the total of currently posted payment records for the selected period. Total Reversed is shown separately from reversal history; do not add it to Gross Collected to reconstruct original collections.',
  mileage_summary: 'Rounding: Trip, vehicle, and rate-period deductions are displayed independently to the nearest cent. Their displayed sums may differ from the total mileage deduction by $0.01.',
  expenses_summary: 'Categories are bookkeeping labels saved in MyTool NOW and are not authoritative tax classifications. Transaction detail below supports the reported totals.'
};

const formatCurrency = (value) => Number(value || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const fmtVal = (val) => {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'object') return JSON.stringify(val);
  const str = String(val);
  if (str.match(/^\d{4}-\d{2}-\d{2}T/)) return new Date(val).toLocaleDateString();
  const n = Number(val);
  if (!isNaN(n) && str.includes('.')) {
    return formatCurrency(n);
  }
  return str;
};

const REPORT_COLUMN_LABELS = {
  invoice_number: 'Invoice',
  estimate_number: 'Estimate',
  invoice_date: 'Date',
  payment_date: 'Date',
  due_date: 'Due',
  createdAt: 'Date',
  deleted_at: 'Date',
  reversal_date: 'Date',
  customer: 'Customer',
  contactPerson: 'Contact',
  amount_paid: 'Paid',
  balance_due: 'Balance',
  aging_status: 'Status',
  payment_method: 'Method',
  payment_ref: 'Reference',
  business_purpose: 'Purpose',
  invoice_count: 'Invoices',
  collected_revenue: 'Revenue',
  invoices_with_payments: 'Paid Invoices',
  expirationDate: 'Expires',
  isActive: 'Active',
  qtyOnHand: 'On Hand',
  reorderPoint: 'Reorder At',
  minStock: 'Minimum',
  maxStock: 'Maximum',
  recorded_qty: 'Recorded',
  counted_qty: 'Counted',
  taxable_sales: 'Taxable',
  nontaxable_sales: 'Nontaxable',
  stateTaxAmount: 'State Tax',
  countyTaxAmount: 'Local Tax',
  totalTaxAmount: 'Total Tax',
  sales_tax: 'Sales Tax',
  invoice_total: 'Total',
  payment_total: 'Total',
  state_tax_collected: 'State Tax',
  county_tax_collected: 'County / Local Tax',
  total_tax_collected: 'Total Tax',
  state_abbr: 'State',
  partName: 'Item'
};

const DETAIL_COLUMN_LABELS = {
  outstanding_invoices: { total: 'Total' },
  quarterly_sales_tax: { payment_date: 'Payment Date' },
  declined_estimates: { total: 'Total' },
  discounts_coupons_usage: { total: 'Total' }
};

const TABLE_DATE_KEYS = new Set([
  'date',
  'invoice_date',
  'payment_date',
  'due_date',
  'createdAt',
  'deleted_at',
  'reversal_date',
  'expirationDate'
]);

const TABLE_MONEY_KEYS = new Set([
  'gross_sales',
  'taxable_sales',
  'nontaxable_sales',
  'stateTaxAmount',
  'countyTaxAmount',
  'totalTaxAmount',
  'tax_collected',
  'sales_tax',
  'invoice_total',
  'payment_total',
  'state_tax_collected',
  'county_tax_collected',
  'total_tax_collected'
]);

const LEFT_ALIGNED_TABLE_COLUMNS = new Set([
  'description',
  'notes',
  'reason',
  'address',
  'email',
  'from',
  'to',
  'business_purpose',
  'purpose'
]);

const labelify = (key) => key
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

const formatCompactTableDate = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return fmtVal(value);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    .toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
};

const fmtTableVal = (key, value) => {
  if (value === null || value === undefined) return '—';
  if (key === 'invoice_number') return String(value).replace(/^INV-/i, '');
  if (key === 'estimate_number') return String(value).replace(/^EST-/i, '');
  if (TABLE_DATE_KEYS.has(key)) return formatCompactTableDate(value);
  if (TABLE_MONEY_KEYS.has(key) && Number.isFinite(Number(value))) {
    return formatCurrency(value);
  }
  return fmtVal(value);
};

const getMileageVehicleLabel = (row, prefix = '') => {
  const rawUnitNumber = String(row[`${prefix}unit_number`] || '').trim();
  const unitNumber = rawUnitNumber === 'UNASSIGNED / HISTORICAL'
    ? ''
    : rawUnitNumber;
  const details = [
    row[`${prefix}year`],
    row[`${prefix}make`],
    row[`${prefix}model`]
  ]
    .map(value => String(value || '').trim())
    .filter(Boolean)
    .join(' ');

  const description = [
    unitNumber ? `UNIT # ${unitNumber}` : '',
    details
  ].filter(Boolean).join(' — ');

  return description || 'UNASSIGNED / HISTORICAL';
};

const getVehicleReportRows = (data) => (
  (Array.isArray(data?.by_vehicle) ? data.by_vehicle : []).map((row) => ({
    vehicle: getMileageVehicleLabel(row),
    trips: row.trip_count,
    miles: `${Number(row.miles || 0).toFixed(2)} mi`,
    deduction: formatCurrency(row.deductible_amount)
  }))
);

const getMileageTripRows = (data) => (
  (Array.isArray(data?.rows) ? data.rows : [])
    .map((row) => ({
      date: row.date,
      vehicle: getMileageVehicleLabel(row, 'vehicle_'),
      from: row.startAddress || '—',
      to: row.endAddress || '—',
      business_purpose: row.purpose || '—',
      miles: `${Number(row.distance || 0).toFixed(2)} mi`,
      rate: `$${Number(row.rate_applied || 0).toFixed(3)}/mile`,
      deduction: formatCurrency(
        Number(row.distance || 0) * Number(row.rate_applied || 0)
      )
    }))
    .sort((a, b) =>
      String(a.date || '').localeCompare(String(b.date || '')) ||
      a.vehicle.localeCompare(b.vehicle) ||
      a.from.localeCompare(b.from) ||
      a.to.localeCompare(b.to) ||
      a.business_purpose.localeCompare(b.business_purpose)
    )
);

const getMileageRateDisplay = (data) => {
  const breakdown = Array.isArray(data?.rate_breakdown) ? data.rate_breakdown : [];
  const multiple = data?.multiple_rates_applied === true;
  const directRate = data?.meta?.irs_rate_per_mile;
  const normalizedRate = directRate == null
    ? (breakdown.length === 1 ? Number(breakdown[0]?.rate) : null)
    : Number(directRate);
  const singleRate = Number.isFinite(normalizedRate) ? normalizedRate : null;

  return {
    breakdown,
    multiple,
    label: multiple
      ? 'Multiple IRS Rates Applied'
      : singleRate == null
        ? null
        : `IRS Rate: $${singleRate.toFixed(3)}/mile`
  };
};

const getProfitLossExpenseRows = (data) => {
  const grouped = new Map();

  (Array.isArray(data?.by_category) ? data.by_category : []).forEach((row) => {
    const category = String(row.category || '').trim() || 'Uncategorized';
    const totalCents = Math.round(Number(row.total || 0) * 100);

    grouped.set(
      category,
      (grouped.get(category) || 0) + totalCents
    );
  });

  return Array.from(grouped, ([category, totalCents]) => ({
    category,
    totalCents
  }));
};

const getTopCustomerRows = (data) => (
  (Array.isArray(data?.rows) ? data.rows : []).map((row) => ({
    customer: row.customer,
    collected_revenue: row.total_revenue,
    invoices_with_payments: row.invoice_count
  }))
);

const getInventoryCountRows = (data) => (
  (Array.isArray(data?.rows) ? data.rows : []).map((row) => ({
    item: row.partName,
    recorded_qty: row.qtyOnHand,
    counted_qty: '________________',
    variance: '________________',
    notes: '____________________________'
  }))
);

const getNoResponseEstimateRows = (data) => (
  (Array.isArray(data?.rows) ? data.rows : []).map((row) => ({
    estimate_number: row.estimate_number,
    createdAt: row.createdAt,
    customer: row.customer,
    total: row.total
  }))
);

const getSalesSummaryRows = (data) => (
  (Array.isArray(data?.rows) ? data.rows : []).map((row) => ({
    invoice_number: row.invoice_number,
    invoice_date: row.invoice_date,
    customer: row.customer,
    status: row.status,
    invoice_total: row.total,
    amount_paid: row.amount_paid,
    balance_due: row.balance_due
  }))
);

const getCustomerListRows = (data) => (
  (Array.isArray(data?.rows) ? data.rows : []).map((row) => ({
    customer: row.name,
    contactPerson: row.contactPerson,
    phone: row.phone,
    email: row.email,
    address: [
      row.street,
      [row.city, row.state, row.zip].filter(Boolean).join(' ')
    ].filter(Boolean).join(', '),
    terms: row.terms
  }))
);

const getPaymentHistoryRows = (data) => (
  (Array.isArray(data?.rows) ? data.rows : []).map((row) => ({
    payment_date: row.payment_date,
    amount: row.amount,
    payment_method: row.payment_method,
    payment_ref: row.payment_ref,
    invoice_number: row.invoice_number,
    customer: row.customer
  }))
);

const getQuarterlyStateRows = (data) => (
  (Array.isArray(data?.by_state) ? data.by_state : []).map((row) => ({
    state: row.state_abbr || row.state_name,
    invoice_count: row.invoice_count,
    state_tax_collected: row.state_tax_collected,
    county_tax_collected: row.county_tax_collected,
    total_tax_collected: row.total_tax_collected
  }))
);

const getQuarterlyCountyRows = (data) => (
  (Array.isArray(data?.by_county) ? data.by_county : []).map((row) => ({
    state: row.state_abbr || row.state_name,
    county: row.county_name === 'No County Tax' ? 'N/A' : row.county_name,
    rate: row.rate,
    invoice_count: row.invoice_count,
    state_tax_collected: row.state_tax_collected,
    county_tax_collected: row.county_tax_collected,
    total_tax_collected: row.total_tax_collected
  }))
);

const getQuarterlySalesTaxRows = (data) => (
  (Array.isArray(data?.rows) ? data.rows : []).map((row) => ({
    invoice_number: row.invoice_number,
    payment_date: row.payment_date,
    customer: row.customer,
    gross_sales: row.gross_sales,
    taxable_sales: row.taxable_sales,
    nontaxable_sales: row.nontaxable_sales,
    stateTaxAmount: row.stateTaxAmount,
    countyTaxAmount: row.countyTaxAmount,
    totalTaxAmount: row.totalTaxAmount,
    payment_total: row.total
  }))
);

const ReportsSettings = ({ navigate, isPhone, reportId, onReportOpened }) => {
  const [isMobile, setIsMobile] = useState(getIsMobile());
  const [generating, setGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [reportPeriod, setReportPeriod] = useState('monthly');
  const [reportData, setReportData] = useState(null);
  const [reportError, setReportError] = useState(null);
  const [reportLabel, setReportLabel] = useState('');
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [lastGeneratedAt, setLastGeneratedAt] = useState(null);
  const [businessName, setBusinessName] = useState('');

  const printRef = useRef(null);
  const viewerTopRef = useRef(null);
  const openedReportIdRef = useRef(null);

  const API_BASE = useMemo(() => getApiBase(), []);

  useEffect(() => {
    const fetchBusinessName = async () => {
      try {
        const res = await fetch(getApiBase() + '/api/settings/business', {
          headers: { ...getTenantHeaders() }
        });
        if (res.ok) {
          const data = await res.json();
          setBusinessName(data.businessName || '');
        }
      } catch (e) { console.error(e); }
    };
    fetchBusinessName();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    setIsMobile(getIsMobile());
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!reportId) {
      openedReportIdRef.current = null;
      return;
    }
    if (openedReportIdRef.current === reportId) return;

    const report = REPORT_CATEGORIES
      .flatMap((category) => category.reports)
      .find((candidate) => candidate.id === reportId);
    if (!report) return;

    openedReportIdRef.current = reportId;
    setSelectedReport(report);
    onReportOpened?.();
  }, [reportId, onReportOpened]);

  const getDateParams = () => {
    const now = new Date();
    if (reportPeriod === 'monthly') {
      return {
        from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10),
        to: now.toISOString().slice(0, 10)
      };
    }
    if (reportPeriod === 'annual') {
      return { from: now.getFullYear() + '-01-01', to: now.toISOString().slice(0, 10) };
    }
    return { from: dateRange.start, to: dateRange.end };
  };

  const needsDateRange = (id) =>
    ['sales_summary', 'expenses_summary', 'mileage_summary', 'profit_loss', 'payment_history', 'schedule_c', 'discounts_coupons_usage', 'audit_trail'].includes(id);

  const noDateRange = (id) =>
    ['outstanding_invoices', 'customer_list', 'top_customers', 'declined_estimates', 'no_response_estimates', 'inventory_reorder', 'inventory_count_sheet', 'discounts_coupons_catalog'].includes(id);

  const scrollToViewer = () => {
    try {
      if (viewerTopRef.current) {
        viewerTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (_) {}
  };

  const handleGenerateReport = async (report) => {
    if (!LIVE_REPORT_IDS.includes(report.id)) {
      setSelectedReport(null);
      alert('"' + report.name + '" is coming soon!');
      return;
    }

    setGenerating(true);
    setReportData(null);
    setReportError(null);
    setReportLabel(report.name);
    setLastGeneratedAt(null);

    try {
      const { from, to } = getDateParams();
      let params;

      if (report.id === 'quarterly_sales_tax') {
        params = new URLSearchParams({ type: report.id, quarter: selectedQuarter, year: String(selectedYear) });
      } else if (noDateRange(report.id)) {
        params = new URLSearchParams({ type: report.id });
      } else {
        params = new URLSearchParams({ type: report.id, from, to });
      }

      const url = API_BASE + '/api/reports/generate?' + params.toString();

      const res = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          ...getTenantHeaders(),
          'Cache-Control': 'no-cache'
        }
      });

      if (!res.ok) {
        let msg = 'Server error: ' + res.status;
        try {
          const text = await res.text();
          if (text) msg = msg + ' - ' + text;
        } catch (_) {}
        throw new Error(msg);
      }

      const json = await res.json();
      setReportData(json);
      setLastGeneratedAt(new Date());
      setSelectedReport(null);
      setTimeout(scrollToViewer, 50);
    } catch (err) {
      console.error('Report generation error:', err);
      setReportError(err.message || 'Failed to generate report.');
      setSelectedReport(null);
      setTimeout(scrollToViewer, 50);
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank');
    const quarterlyHeader = reportData?.meta?.type === 'quarterly_sales_tax'
      ? `<h1>QUARTERLY SALES TAX REPORT</h1><div class="meta">${reportData.meta.quarter} ${reportData.meta.year} — ${reportData.meta.from} through ${reportData.meta.to}<br>Cash Basis — Based on Posted Payments</div>`
      : '';
    win.document.write(
      '<html><head><title>' +
        reportLabel +
        '</title><style>@page{size:landscape;}div{overflow:visible!important;}body{font-family:Arial Black,sans-serif;padding:24px;background:white;color:black;}h1{font-size:20px;font-weight:900;text-transform:uppercase;margin-bottom:4px;}.meta{font-size:12px;font-weight:700;color:#555;margin-bottom:16px;}h3{font-size:13px;font-weight:900;text-transform:uppercase;border-bottom:2px solid black;padding-bottom:4px;margin:16px 0 8px;}table{width:100%;border-collapse:collapse;font-size:9px;margin-bottom:16px;}th{background:black;color:white;padding:7px;text-align:left;font-weight:900;text-transform:uppercase;font-size:10px;}td{padding:6px 7px;border-bottom:1px solid #ccc;}tr:nth-child(even) td{background:#f5f5f5;}.chip{display:inline-block;border:2px solid black;padding:6px 12px;margin:4px;font-size:12px;font-weight:900;max-width:100%;box-sizing:border-box;overflow-wrap:anywhere;}</style></head><body>' +
      '<div style="font-weight:900;font-size:22px;text-transform:uppercase;margin-bottom:8px;">' + businessName + '</div>' +
        quarterlyHeader +
        content.innerHTML +
        '</body></html>'
    );
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 250);
  };

  const styles = {
    page: { padding: isPhone ? '18px 4px' : (isMobile ? '18px' : '40px'), backgroundColor: '#E8E2DA', minHeight: '100vh' },
    contentWrapper: { maxWidth: '1200px', padding: isPhone ? '0' : '0 12px', margin: '0 auto' },
    panel: { backgroundColor: '#E7B888', border: isMobile ? '6px solid black' : '10px solid black', padding: isPhone ? '15px 5px' : (isMobile ? '15px' : '40px'), marginBottom: '18px' },
    categoryTitle: { fontWeight: 900, textTransform: 'uppercase', fontSize: '18px', marginTop: '24px', marginBottom: '12px', paddingTop: '16px', borderTop: '4px solid black' },
    firstCategoryTitle: { fontWeight: 900, textTransform: 'uppercase', fontSize: '18px', marginTop: '0', marginBottom: '12px', paddingTop: '0', borderTop: 'none' },
    grid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', marginBottom: '16px' },
    reportCard: { border: '4px solid black', padding: '16px', backgroundColor: 'white', cursor: 'pointer' },
    reportIcon: { fontSize: '32px', marginBottom: '8px' },
    reportName: { fontWeight: 900, fontSize: '18px', marginBottom: '6px', textTransform: 'uppercase' },
    reportDesc: { fontWeight: 700, fontSize: '13px', color: '#666', lineHeight: 1.4 },
    modal: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 5000, padding: '20px' },
    modalContent: { backgroundColor: 'white', border: '8px solid black', padding: '24px', maxWidth: '500px', width: '100%' },
    modalTitle: { fontWeight: 900, fontSize: '22px', marginBottom: '16px', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '10px' },
    label: { fontWeight: 900, textTransform: 'uppercase', fontSize: '13px', marginBottom: '6px', display: 'block', marginTop: '12px' },
    select: { padding: '14px', border: '4px solid black', fontWeight: 900, width: '100%', fontSize: '16px', boxSizing: 'border-box', backgroundColor: 'white' },
    input: { padding: '14px', border: '4px solid black', fontWeight: 900, width: '100%', fontSize: '16px', boxSizing: 'border-box' },
    dateRow: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px', marginTop: '8px' },
    modalBtnRow: { display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' },
    generateBtn: { flex: 1, minWidth: isMobile ? '100%' : undefined, background: 'black', color: '#FFA500', border: '4px solid #E8761D', padding: '12px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' },
    cancelBtn: { flex: 1, minWidth: isMobile ? '100%' : undefined, background: 'white', color: 'black', border: '4px solid black', padding: '12px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' },
    note: { fontWeight: 900, fontSize: '13px', padding: '12px', border: '3px solid black', backgroundColor: '#ffffcc', marginBottom: '12px' },
    disclosure: { fontWeight: 400, fontSize: '11px', lineHeight: 1.5, color: '#444', marginBottom: '12px' },
    success: { fontWeight: 900, fontSize: '13px', padding: '12px', border: '3px solid black', backgroundColor: '#f0fff4', marginBottom: '12px' }
  };

  const renderTable = (rows, { fitDesktop = false, mobileMinWidth, columnLabels = {}, centerColumns = [] } = {}) => {
    if (!rows || rows.length === 0) return null;
    const cols = Object.keys(rows[0]);
    const useDesktopFit = fitDesktop && !isMobile;
    return (
      <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
        <table style={{ width: '100%', minWidth: isMobile && fitDesktop ? mobileMinWidth : undefined, tableLayout: useDesktopFit ? 'fixed' : 'auto', borderCollapse: 'collapse', fontSize: isMobile ? '12px' : '13px' }}>
          <thead>
            <tr>
              {cols.map((col) => (
                <th key={col} style={{ background: 'black', color: 'white', padding: '7px 6px', textAlign: LEFT_ALIGNED_TABLE_COLUMNS.has(col) ? 'left' : 'center', fontWeight: 900, textTransform: 'uppercase', fontSize: '11px', lineHeight: 1.15, whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'normal' }}>
                  {columnLabels[col] || REPORT_COLUMN_LABELS[col] || labelify(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ backgroundColor: i % 2 === 0 ? 'white' : '#f5f5f5' }}>
                {cols.map((col) => (
                  <td key={col} style={{ padding: '7px 6px', borderBottom: '1px solid #ddd', fontWeight: 700, textAlign: LEFT_ALIGNED_TABLE_COLUMNS.has(col) ? 'left' : 'center', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'normal' }}>
                    {fmtTableVal(col, row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderQuarterlyState = state => (
    <section key={`${state.state_abbr}-${state.state_name}`} style={{ marginTop: '24px', borderTop: '5px solid black', paddingTop: '12px' }}>
      <h3 style={{ fontWeight: 900, fontSize: '16px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '6px' }}>
        {state.state_name}{state.is_default ? ' — DEFAULT STATE' : ''} — TOTAL SALES TAX COLLECTED: {formatCurrency(state.total_tax_collected)}
      </h3>
      <div style={{ fontWeight: 900 }}>State Tax: {formatCurrency(state.state_tax_collected)}</div>
      <div style={{ fontWeight: 900 }}>County / Local / Municipality: {formatCurrency(state.county_tax_collected)}</div>
      <h3 style={{ fontWeight: 900, fontSize: '14px', marginTop: '18px' }}>COUNTY / LOCAL / MUNICIPALITY</h3>
      {renderTable(state.jurisdictions, {
        columnLabels: { name: 'Jurisdiction', invoice_count: 'Invoices', tax_collected: 'Tax Collected' }
      })}
      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '3px solid black', paddingTop: '6px', fontWeight: 900 }}>
        <span>TOTAL</span><span>{formatCurrency(state.county_tax_collected)}</span>
      </div>
      <h3 style={{ fontWeight: 900, fontSize: '14px', marginTop: '18px' }}>{state.state_name} — PAYMENT / INVOICE DETAIL</h3>
      {renderTable(getQuarterlySalesTaxRows({ rows: state.rows }), {
        fitDesktop: true,
        mobileMinWidth: '900px',
        columnLabels: DETAIL_COLUMN_LABELS.quarterly_sales_tax
      })}
    </section>
  );

  const renderProfitLoss = (data) => {
    const isScheduleC = data.meta?.type === 'schedule_c';
    const totals = data.totals || {};
    const expenseRows = getProfitLossExpenseRows(data);
    const money = formatCurrency;
    const revenue = isScheduleC
      ? totals.gross_receipts
      : totals.total_revenue;
    const sectionTitle = {
      fontWeight: 900,
      fontSize: '14px',
      textTransform: 'uppercase',
      borderBottom: '3px solid black',
      paddingBottom: '4px',
      margin: '18px 0 8px'
    };
    const line = {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '20px',
      flexWrap: 'wrap',
      minWidth: 0,
      overflowWrap: 'normal',
      wordBreak: 'normal',
      padding: '7px 4px',
      borderBottom: '1px solid #ccc',
      fontWeight: 700
    };
    const totalLine = {
      ...line,
      borderTop: '3px solid black',
      borderBottom: '3px double black',
      fontWeight: 900
    };

    return (
      <>
        <h3 style={sectionTitle}>{isScheduleC ? 'BUSINESS INCOME' : 'REVENUE'}</h3>
        {isScheduleC && (
          <div style={line}>
            <span>Cash-Basis Business Income</span>
            <span style={{ whiteSpace: 'nowrap' }}>{money(revenue)}</span>
          </div>
        )}
        {!isScheduleC && (
          <div style={totalLine}>
            <span>TOTAL REVENUE</span>
            <span style={{ whiteSpace: 'nowrap' }}>{money(revenue)}</span>
          </div>
        )}

        <h3 style={sectionTitle}>{isScheduleC ? 'BUSINESS EXPENSES' : 'OPERATING EXPENSES'}</h3>
        {expenseRows.length > 0 ? expenseRows.map((row) => (
          <div key={row.category} style={line}>
            <span>{row.category}</span>
            <span style={{ whiteSpace: 'nowrap' }}>{money(row.totalCents / 100)}</span>
          </div>
        )) : (
          <div style={line}>
            <span>{isScheduleC ? 'No business expenses' : 'No operating expenses'}</span>
            <span>$0.00</span>
          </div>
        )}
        <div style={totalLine}>
          <span>{isScheduleC ? 'TOTAL BUSINESS EXPENSES' : 'TOTAL OPERATING EXPENSES'}</span>
          <span style={{ whiteSpace: 'nowrap' }}>{money(totals.total_expenses)}</span>
        </div>

        <h3 style={sectionTitle}>{isScheduleC ? 'MILEAGE DEDUCTION' : 'OTHER DEDUCTIBLE ADJUSTMENT'}</h3>
        {isScheduleC && (
          <div style={line}>
            <span>Business Miles</span>
            <span>{Number(totals.mileage_miles || 0).toFixed(2)} mi</span>
          </div>
        )}
        <div style={line}>
          <span>Mileage Deduction</span>
          <span style={{ whiteSpace: 'nowrap' }}>{money(totals.mileage_deduction)}</span>
        </div>

        <div style={{ ...totalLine, marginTop: '18px' }}>
          <span>TOTAL DEDUCTIONS</span>
          <span style={{ whiteSpace: 'nowrap' }}>{money(totals.total_deductions)}</span>
        </div>

        <div style={{ ...totalLine, marginTop: '18px', fontSize: '18px' }}>
          <span>{isScheduleC ? 'NET BUSINESS PROFIT / LOSS' : 'NET PROFIT / LOSS BEFORE TAXES'}</span>
          <span style={{ whiteSpace: 'nowrap' }}>{money(totals.net_profit)}</span>
        </div>
      </>
    );
  };

  const renderViewer = () => {
    if (!reportData && !reportError) return null;
    const mileageRateDisplay = reportData?.meta?.type === 'mileage_summary'
      ? getMileageRateDisplay(reportData)
      : null;
    return (
      <div ref={viewerTopRef} style={{ backgroundColor: '#fff7ed', border: isMobile ? '6px solid black' : '10px solid black', padding: isMobile ? '15px' : '40px', marginBottom: '18px' }}>
        {lastGeneratedAt && (
          <div style={styles.success}>
            ✅ REPORT GENERATED — {lastGeneratedAt.toLocaleString()}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
          <div>
            {businessName && (
              <div style={{ fontWeight: 900, fontSize: '22px', textTransform: 'uppercase', marginBottom: '4px' }}>{businessName}</div>
            )}
            <div style={{ fontWeight: 900, fontSize: '20px', textTransform: 'uppercase' }}>
              {reportData?.meta?.type === 'schedule_c' ? 'SCHEDULE C BUSINESS SUMMARY' : reportLabel}
            </div>
            {reportData && reportData.meta && (
              <div style={{ fontWeight: 700, fontSize: '13px', color: '#555', marginTop: '4px' }}>
                {reportData.meta.quarter ? reportData.meta.quarter + ' ' + reportData.meta.year : reportData.meta.from ? reportData.meta.from + ' to ' + reportData.meta.to : 'As of ' + new Date().toLocaleDateString()}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button className="jf-report-hover" style={styles.generateBtn} onClick={handlePrint}>🖨️ PRINT</button>
            <button className="jf-report-hover" style={styles.cancelBtn} onClick={() => { setReportData(null); setReportError(null); setLastGeneratedAt(null); }}>
              ✕ CLOSE
            </button>
          </div>
        </div>

        {reportError && (
          <div style={{ padding: '16px', border: '4px solid #cc0000', backgroundColor: '#fff0f0', fontWeight: 900, color: '#cc0000' }}>
            ⚠️ {reportError}
          </div>
        )}

        {reportData && (
          <div ref={printRef}>
            {REPORT_DISCLOSURES[reportData.meta?.type] && (
              <div style={styles.disclosure}>
                {REPORT_DISCLOSURES[reportData.meta.type]}
              </div>
            )}
            {reportData.meta?.type === 'schedule_c' && (
              <div style={styles.disclosure}>
                This Schedule C-oriented business summary is provided for tax-preparation records. It is not a filed IRS Schedule C or tax advice. Review expense classifications and deductions with a qualified tax professional. Supporting records: See Payment History, Expense Summary, and Mileage Summary for supporting detail.
              </div>
            )}
            {mileageRateDisplay?.label && reportData.meta?.type !== 'mileage_summary' && (
              <div style={{ ...styles.note, backgroundColor: 'white' }}>
                {mileageRateDisplay.label}
              </div>
            )}
            {reportData.meta?.type === 'mileage_summary' && mileageRateDisplay?.breakdown.length > 0 && (
              <>
                <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>RATE BREAKDOWN</h3>
                <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '12px' : '13px' }}>
                    <thead>
                      <tr>
                        {['EFFECTIVE DATE', 'IRS RATE', 'MILES', 'DEDUCTION'].map(label => (
                          <th key={label} style={{ background: 'black', color: 'white', padding: '8px 10px', textAlign: 'left', fontWeight: 900, fontSize: '11px', whiteSpace: 'nowrap' }}>{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {mileageRateDisplay.breakdown.map((row, index) => (
                        <tr key={row.mileageRateId || index} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f5f5f5' }}>
                          <td style={{ padding: '7px 10px', borderBottom: '1px solid #ddd', fontWeight: 700 }}>{row.effectiveDate}</td>
                          <td style={{ padding: '7px 10px', borderBottom: '1px solid #ddd', fontWeight: 700 }}>${Number(row.rate).toFixed(3)}/mile</td>
                          <td style={{ padding: '7px 10px', borderBottom: '1px solid #ddd', fontWeight: 700 }}>{Number(row.miles).toFixed(2)} mi</td>
                          <td style={{ padding: '7px 10px', borderBottom: '1px solid #ddd', fontWeight: 700 }}>{formatCurrency(row.deduction)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {reportData.totals && Object.keys(reportData.totals).length > 0 && (
              (reportData.meta?.type === 'profit_loss' || reportData.meta?.type === 'schedule_c') ? (
                renderProfitLoss(reportData)
              ) : reportData.meta?.type === 'sales_summary' ? (
                <>
                  <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>ALL INVOICE ACTIVITY</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                    {[{ k: 'gross_invoiced', label: 'INVOICED REVENUE (EXCLUDING SALES TAX)' }, { k: 'total_invoices', label: 'TOTAL INVOICES' }, { k: 'paid_count', label: 'PAID' }, { k: 'unpaid_count', label: 'UNPAID' }].map(({ k, label }) => (
                      <div key={k} style={{ border: '3px solid black', padding: '10px 16px', backgroundColor: 'white', minWidth: '120px' }}>
                        <div style={{ fontWeight: 900, fontSize: '18px' }}>{fmtVal(reportData.totals[k])}</div>
                        <div style={{ fontWeight: 700, fontSize: '11px', color: '#555', textTransform: 'uppercase', marginTop: '2px' }}>{label}</div>
                      </div>
                    ))}
                  </div>
                  <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>COLLECTIONS TO DATE FOR THESE INVOICES</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                    <div style={{ border: '3px solid #E8761D', padding: '10px 16px', backgroundColor: 'white', minWidth: '120px' }}>
                      <div style={{ fontWeight: 900, fontSize: '18px' }}>{fmtVal(reportData.totals.collected_revenue)}</div>
                      <div style={{ fontWeight: 700, fontSize: '11px', color: '#555', textTransform: 'uppercase', marginTop: '2px' }}>REVENUE COLLECTED TO DATE</div>
                    </div>
                    <div style={{ border: '3px solid black', padding: '10px 16px', backgroundColor: 'white', minWidth: '120px' }}>
                      <div style={{ fontWeight: 900, fontSize: '18px' }}>{fmtVal(reportData.totals.collected_sales_tax)}</div>
                      <div style={{ fontWeight: 700, fontSize: '11px', color: '#555', textTransform: 'uppercase', marginTop: '2px' }}>SALES TAX COLLECTED TO DATE</div>
                    </div>
                  </div>
                  <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>CURRENT OUTSTANDING FOR THESE INVOICES</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                    {[{ k: 'accounts_receivable', label: 'ACCOUNTS RECEIVABLE' }, { k: 'uncollected_sales_tax', label: 'UNCOLLECTED SALES TAX' }].map(({ k, label }) => (
                      <div key={k} style={{ border: '3px solid black', padding: '10px 16px', backgroundColor: 'white', minWidth: '120px' }}>
                        <div style={{ fontWeight: 900, fontSize: '18px' }}>{fmtVal(reportData.totals[k])}</div>
                        <div style={{ fontWeight: 700, fontSize: '11px', color: '#555', textTransform: 'uppercase', marginTop: '2px' }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : reportData.meta?.type === 'quarterly_sales_tax' ? (
                <>
                  <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>SUMMARY</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                    {[
                      { k: 'total_payments_received', label: 'TOTAL PAYMENTS RECEIVED' },
                      { k: 'gross_sales', label: 'GROSS SALES (EXCLUDING SALES TAX)' },
                      { k: 'taxable_sales', label: 'TAXABLE SALES' },
                      { k: 'nontaxable_sales', label: 'NON-TAXABLE / EXEMPT SALES' },
                      { k: 'state_tax_collected', label: 'STATE SALES TAX COLLECTED' },
                      { k: 'county_tax_collected', label: 'COUNTY / LOCAL SALES TAX COLLECTED' },
                      { k: 'total_tax_collected', label: 'TOTAL SALES TAX COLLECTED' }
                    ].map(({ k, label }) => (
                      <div key={k} style={{ flex: '1 1 180px', border: '3px solid black', padding: '10px 16px', backgroundColor: 'white', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
                        <div style={{ fontWeight: 900, fontSize: 'clamp(14px, 4vw, 18px)', maxWidth: '100%', minWidth: 0, whiteSpace: 'nowrap' }}>{formatCurrency(reportData.totals[k])}</div>
                        <div style={{ fontWeight: 700, fontSize: '11px', color: '#555', textTransform: 'uppercase', marginTop: '2px' }}>{label}</div>
                      </div>
                    ))}
                  </div>
                  {(reportData.by_state || []).map(renderQuarterlyState)}
                </>
              ) : reportData.meta?.type === 'mileage_summary' ? (
                <>
                  <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>SUMMARY</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                    {[
                      { label: 'TOTAL TRIPS', value: reportData.totals.total_trips },
                      { label: 'BUSINESS MILES', value: `${Number(reportData.totals.total_miles || 0).toFixed(2)} mi` },
                      { label: 'MILEAGE DEDUCTION', value: formatCurrency(reportData.totals.deductible_amount) }
                    ].map(({ label, value }) => (
                      <div key={label} style={{ border: '3px solid black', padding: '10px 16px', backgroundColor: 'white', minWidth: '120px' }}>
                        <div style={{ fontWeight: 900, fontSize: '18px' }}>{value}</div>
                        <div style={{ fontWeight: 700, fontSize: '11px', color: '#555', textTransform: 'uppercase', marginTop: '2px' }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : reportData.meta?.type === 'inventory_count_sheet' ? (
                <>
                  <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>SUMMARY</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                    {[
                      { label: 'TOTAL ITEMS', value: reportData.totals.total_items },
                      { label: 'RECORDED UNITS', value: reportData.totals.total_units }
                    ].map(({ label, value }) => (
                      <div key={label} style={{ border: '3px solid black', padding: '10px 16px', backgroundColor: 'white', minWidth: '120px' }}>
                        <div style={{ fontWeight: 900, fontSize: '18px' }}>{fmtVal(value)}</div>
                        <div style={{ fontWeight: 700, fontSize: '11px', color: '#555', textTransform: 'uppercase', marginTop: '2px' }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>SUMMARY</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                    {Object.entries(reportData.totals)
                      .filter(([k]) => reportData.meta?.type !== 'payment_history' || k !== 'net_collected')
                      .map(([k, v]) => (
                      <div key={k} style={{ border: '3px solid black', padding: '10px 16px', backgroundColor: 'white', minWidth: '120px' }}>
                        <div style={{ fontWeight: 900, fontSize: '18px' }}>
                          {reportData.meta?.type === 'schedule_c' && k === 'mileage_miles'
                            ? `${Number(v || 0).toFixed(2)} mi`
                            : reportData.meta?.type === 'mileage_summary' && k === 'total_miles'
                            ? `${Number(v || 0).toFixed(2)} mi`
                            : fmtVal(v)}
                        </div>
                        <div style={{ fontWeight: 700, fontSize: '11px', color: '#555', textTransform: 'uppercase', marginTop: '2px' }}>
                          {reportData.meta?.type === 'schedule_c' && k === 'mileage_miles' ? 'TOTAL MILES' : labelify(k)}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )
            )}

            {(reportData.meta?.type === 'schedule_c' || reportData.meta?.type === 'profit_loss') && (
              <div style={{ padding: '12px 16px', border: '3px solid #cc8800', backgroundColor: '#fffbe6', fontWeight: 700, fontSize: '12px', marginBottom: '16px' }}>
                ⚠️ The IRS requires you to choose ONE deduction method for vehicle expenses — either the standard mileage rate OR actual vehicle expenses such as fuel and repairs. You cannot deduct both. If you have logged mileage AND recorded vehicle expenses, share these reports with your tax professional at filing time.
              </div>
            )}

            {(reportData.meta?.type === 'declined_estimates' || reportData.meta?.type === 'no_response_estimates') && (
              <div style={{ padding: '12px 16px', border: '3px solid #0077cc', backgroundColor: '#e8f4ff', fontWeight: 700, fontSize: '12px', marginBottom: '16px' }}>
                ℹ️ Totals shown include sales tax. These figures represent the full estimate amount, not realized revenue.
              </div>
            )}

            {reportData.meta?.type !== 'profit_loss' && reportData.meta?.type !== 'schedule_c' && Array.isArray(reportData.by_category) && reportData.by_category.length > 0 && (
              <>
                <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>BY CATEGORY</h3>
                {renderTable(reportData.by_category)}
              </>
            )}

            {Array.isArray(reportData.by_vendor) && reportData.by_vendor.length > 0 && (
              <>
                <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>{reportData.meta?.type === 'expenses_summary' ? 'TOP VENDORS — UP TO 20' : 'BY VENDOR'}</h3>
                {renderTable(reportData.by_vendor)}
              </>
            )}

            {reportData.meta?.type !== 'mileage_summary' && Array.isArray(reportData.by_purpose) && reportData.by_purpose.length > 0 && (
              <>
                <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>BY PURPOSE</h3>
                {renderTable(reportData.by_purpose)}
              </>
            )}

            {reportData.meta?.type === 'mileage_summary' && getVehicleReportRows(reportData).length > 0 && (
              <>
                <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>VEHICLE SUMMARY</h3>
                {renderTable(getVehicleReportRows(reportData))}
              </>
            )}

            {Array.isArray(reportData.by_method) && reportData.by_method.length > 0 && (
              <>
                <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>BY PAYMENT METHOD</h3>
                {renderTable(reportData.by_method)}
              </>
            )}

            {Array.isArray(reportData.deletions) && reportData.deletions.length > 0 && (
              <>
                <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px', color: '#cc0000' }}>INVOICE DELETIONS LOG ({reportData.deletions.length})</h3>
                {renderTable(reportData.deletions)}
              </>
            )}

            {Array.isArray(reportData.reversals) && reportData.reversals.length > 0 && (
              <>
                <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px', color: '#cc0000' }}>PAYMENT REVERSALS ({reportData.reversals.length})</h3>
                {renderTable(reportData.reversals)}
              </>
            )}

            {reportData.meta?.type !== 'quarterly_sales_tax' && Array.isArray(reportData.by_state) && reportData.by_state.length > 0 && (
              <>
                <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>BY STATE</h3>
                {renderTable(getQuarterlyStateRows(reportData), {
                  fitDesktop: reportData.meta?.type === 'quarterly_sales_tax',
                  mobileMinWidth: '560px',
                  centerColumns: ['invoice_count']
                })}
              </>
            )}
            {reportData.meta?.type !== 'quarterly_sales_tax' && Array.isArray(reportData.by_county) && reportData.by_county.length > 0 && (
              <>
                <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>BY COUNTY</h3>
                {renderTable(getQuarterlyCountyRows(reportData), {
                  centerColumns: ['invoice_count']
                })}
              </>
            )}

            {Array.isArray(reportData.discounts) && reportData.discounts.length > 0 && (
              <>
                <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>DISCOUNTS</h3>
                {renderTable(reportData.discounts)}
              </>
            )}

            {Array.isArray(reportData.coupons) && reportData.coupons.length > 0 && (
              <>
                <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>COUPONS</h3>
                {renderTable(reportData.coupons)}
              </>
            )}

            {reportData.meta?.type !== 'schedule_c' && reportData.meta?.type !== 'quarterly_sales_tax' && Array.isArray(reportData.rows) && reportData.rows.length > 0 && (
              <>
                <h3 style={{ fontWeight: 900, fontSize: '14px', textTransform: 'uppercase', borderBottom: '3px solid black', paddingBottom: '4px', marginBottom: '10px' }}>
                  {reportData.meta?.type === 'mileage_summary'
                    ? `TRIP DETAIL (${reportData.rows.length} TRIPS)`
                    : `DETAIL (${reportData.rows.length} RECORDS)`}
                </h3>
                {renderTable(
                  reportData.meta?.type === 'mileage_summary'
                    ? getMileageTripRows(reportData)
                    : reportData.meta?.type === 'top_customers'
                    ? getTopCustomerRows(reportData)
                    : reportData.meta?.type === 'inventory_count_sheet'
                    ? getInventoryCountRows(reportData)
                    : reportData.meta?.type === 'no_response_estimates'
                    ? getNoResponseEstimateRows(reportData)
                    : reportData.meta?.type === 'sales_summary'
                    ? getSalesSummaryRows(reportData)
                    : reportData.meta?.type === 'customer_list'
                    ? getCustomerListRows(reportData)
                    : reportData.meta?.type === 'payment_history'
                    ? getPaymentHistoryRows(reportData)
                    : reportData.meta?.type === 'quarterly_sales_tax'
                    ? getQuarterlySalesTaxRows(reportData)
                    : reportData.rows,
                  reportData.meta?.type === 'mileage_summary'
                    ? { fitDesktop: true, mobileMinWidth: '760px' }
                    : { columnLabels: DETAIL_COLUMN_LABELS[reportData.meta?.type] || {} }
                )}
              </>
            )}

            {reportData.meta?.type !== 'profit_loss' && reportData.meta?.type !== 'schedule_c' && Array.isArray(reportData.rows) && reportData.rows.length === 0 && (
              <div style={{ padding: '16px', border: '3px solid black', backgroundColor: 'white', fontWeight: 900, textAlign: 'center' }}>
                No records found for this period.
              </div>
            )}

            {!reportData.totals && !reportData.rows && !reportData.discounts && (
              <pre style={{ border: '3px solid black', padding: '12px', backgroundColor: 'white', fontSize: '12px', overflow: 'auto' }}>
                {JSON.stringify(reportData, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.page}>
      <style>{`
        .jf-report-hover:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transform: scale(1.02);
          transition: all 0.12s ease;
        }
        .jf-report-hover:active { transform: translateY(1px); }
        @media (max-width: 1024px) {
          .jf-report-hover:hover { transform: none !important; }
        }
      `}</style>

      <div style={styles.contentWrapper}>
        <TitleBar
          icon="📊"
          title="REPORTS"
          accent="#E8761D"
          right={
            <button
              className="jf-report-hover"
              onClick={() => navigate('/settings')}
              style={{ background: 'black', color: '#FFA500', border: '5px solid #E8761D', padding: '14px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' }}
            >
              BACK TO SETTINGS
            </button>
          }
        />

        {renderViewer()}

        {generating && (
          <div style={{ ...styles.panel, textAlign: 'center', padding: '30px', fontWeight: 900, fontSize: '16px' }}>
            ⏳ GENERATING REPORT...
          </div>
        )}

        <div style={styles.panel}>
          <div style={styles.note}>Generate business reports for accounting, tax filing, and business analysis.</div>

          {REPORT_CATEGORIES.map((category, idx) => (
            <div key={category.category}>
              <h2 style={idx === 0 ? styles.firstCategoryTitle : styles.categoryTitle}>{category.category}</h2>
              <div style={styles.grid}>
                {category.reports.map((report) => (
                  <div key={report.id} className="jf-report-hover" style={styles.reportCard} onClick={() => setSelectedReport(report)}>
                    <div style={styles.reportIcon}>{report.icon}</div>
                    <div style={styles.reportName}>{report.name}</div>
                    <div style={styles.reportDesc}>{report.description}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {selectedReport && (
          <div style={styles.modal} onClick={() => setSelectedReport(null)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>
                <span>{selectedReport.icon}</span>
                {selectedReport.name}
              </h2>

              {!LIVE_REPORT_IDS.includes(selectedReport.id) && (
                <div style={{ ...styles.note, backgroundColor: '#e8f4fd', borderColor: '#2da8ff' }}>
                  🚧 This report is coming soon and will be available in a future update.
                </div>
              )}

              {LIVE_REPORT_IDS.includes(selectedReport.id) && selectedReport.id === 'quarterly_sales_tax' && (
                <>
                  <label style={styles.label}>SELECT QUARTER</label>
                  <select style={styles.select} value={selectedQuarter} onChange={(e) => setSelectedQuarter(e.target.value)}>
                    <option value="Q1">Q1 (JAN-MAR)</option>
                    <option value="Q2">Q2 (APR-JUN)</option>
                    <option value="Q3">Q3 (JUL-SEP)</option>
                    <option value="Q4">Q4 (OCT-DEC)</option>
                  </select>
                  <label style={styles.label}>YEAR</label>
                  <select style={styles.select} value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                    {Array.from(
                      { length: 10 },
                      (_, i) => new Date().getFullYear() - 7 + i
                    ).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </>
              )}

              {LIVE_REPORT_IDS.includes(selectedReport.id) && needsDateRange(selectedReport.id) && (
                <>
                  <label style={styles.label}>REPORT PERIOD</label>
                  <select style={styles.select} value={reportPeriod} onChange={(e) => setReportPeriod(e.target.value)}>
                    <option value="monthly">THIS MONTH</option>
                    <option value="annual">THIS YEAR</option>
                    <option value="custom">CUSTOM DATE RANGE</option>
                  </select>
                  {reportPeriod === 'custom' && (
                    <>
                      <label style={styles.label}>DATE RANGE</label>
                      <div style={styles.dateRow}>
                        <input type="date" style={styles.input} value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} />
                        <input type="date" style={styles.input} value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} />
                      </div>
                    </>
                  )}
                </>
              )}

              {LIVE_REPORT_IDS.includes(selectedReport.id) && noDateRange(selectedReport.id) && (
                <div style={{ ...styles.note, backgroundColor: '#f0fff4', borderColor: '#059669' }}>
                  ✅ This report shows all current data — no date range needed.
                </div>
              )}

              <div style={styles.modalBtnRow}>
                <button
                  className="jf-report-hover"
                  style={LIVE_REPORT_IDS.includes(selectedReport.id) ? styles.generateBtn : { ...styles.generateBtn, opacity: 0.5, cursor: 'not-allowed' }}
                  onClick={() => handleGenerateReport(selectedReport)}
                  disabled={generating || !LIVE_REPORT_IDS.includes(selectedReport.id)}
                >
                  {generating ? 'GENERATING...' : LIVE_REPORT_IDS.includes(selectedReport.id) ? 'GENERATE REPORT' : 'COMING SOON'}
                </button>
                <button className="jf-report-hover" style={styles.cancelBtn} onClick={() => setSelectedReport(null)}>
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsSettings;
